import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, ChevronUp, ChevronDown, ToggleLeft, ToggleRight, Check, X, Loader2, Trash2, Bookmark, FolderInput, Copy, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = window.location.origin;
// Canonical public base for shareable Experience URLs. In production this
// resolves to the deployment domain (https://portal.yulontap.com); when the
// env var is absent it falls back to the current origin so links stay valid
// on any host (matches the platform's resolve_base_url philosophy).
const PUBLIC_BASE = (process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin).replace(/\/$/, '');

// Organization-level Review Shield questions. Every question shown belongs to
// the organization and is fully editable; all actions persist immediately.
// The org's set is shared by all of its Review Shield cards. No card-level
// assignment, no copy-on-write, no separate "Save Changes".
export const QuestionManager = ({ token, organizationId = null }) => {
    const { t, i18n } = useTranslation();
    const primaryLang = ['en', 'fr', 'es'].includes((i18n.language || 'en').split('-')[0]) ? (i18n.language || 'en').split('-')[0] : 'en';
    const langLabel = { en: 'English', fr: 'Français', es: 'Español' }[primaryLang];
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formText, setFormText] = useState('');
    const [saving, setSaving] = useState(false);
    const [experiences, setExperiences] = useState([]);
    const [expName, setExpName] = useState('');
    const [showSaveExp, setShowSaveExp] = useState(false);
    const [busyExp, setBusyExp] = useState(false);

    const headers = { Authorization: `Bearer ${token}` };
    const orgQ = organizationId ? `organization_id=${organizationId}` : '';
    const withOrg = (base) => (orgQ ? `${base}${base.includes('?') ? '&' : '?'}${orgQ}` : base);

    const loadQuestions = useCallback(async () => {
        try {
            const res = await axios.get(withOrg(`${API_BASE}/api/review-shield/questions?include_inactive=true`), { headers });
            setQuestions(res.data);
        } catch (err) {
            console.error('Failed to load questions:', err);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, organizationId]);

    useEffect(() => { loadQuestions(); }, [loadQuestions]);

    const loadExperiences = useCallback(async () => {
        try {
            const res = await axios.get(withOrg(`${API_BASE}/api/review-shield/experiences`), { headers });
            setExperiences(res.data || []);
        } catch (err) { /* non-blocking */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, organizationId]);

    useEffect(() => { loadExperiences(); }, [loadExperiences]);

    // Build a payload from the single primary-language field. On EDIT we blank
    // the other languages so the backend regenerates their translations; on
    // CREATE we omit them so the backend auto-translates.
    const buildPayload = (regenerate) => {
        const p = { text_en: null, text_fr: null, text_es: null };
        p[`text_${primaryLang}`] = formText.trim() || null;
        if (regenerate) {
            ['en', 'fr', 'es'].forEach((l) => { if (l !== primaryLang) p[`text_${l}`] = ''; });
        }
        return p;
    };

    const handleCreate = async () => {
        if (!formText.trim()) return;
        setSaving(true);
        try {
            const res = await axios.post(withOrg(`${API_BASE}/api/review-shield/questions`), buildPayload(false), { headers });
            setQuestions(prev => [...prev, res.data]);
            toast.success(t('reviewShield.questionCreated', 'Question created'));
            resetForm();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create question');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async () => {
        if (!editingId) return;
        setSaving(true);
        try {
            const res = await axios.put(withOrg(`${API_BASE}/api/review-shield/questions/${editingId}`), buildPayload(true), { headers });
            setQuestions(prev => prev.map(q => q.id === editingId ? res.data : q));
            toast.success(t('reviewShield.questionUpdated', 'Question updated'));
            resetForm();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update question');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveExperience = async () => {
        if (!expName.trim()) return;
        setBusyExp(true);
        try {
            const res = await axios.post(withOrg(`${API_BASE}/api/review-shield/experiences`), { name: expName.trim() }, { headers });
            setExperiences(prev => [res.data, ...prev]);
            setExpName('');
            setShowSaveExp(false);
            toast.success(t('reviewShield.experienceSaved', 'Question Set saved'));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save experience');
        } finally {
            setBusyExp(false);
        }
    };

    const handleApplyExperience = async (exp) => {
        if (!window.confirm(t('reviewShield.confirmApplyExp', `Apply "${exp.name}"? This replaces the current questions for this organization.`))) return;
        setBusyExp(true);
        try {
            const res = await axios.post(withOrg(`${API_BASE}/api/review-shield/experiences/${exp.id}/apply`), {}, { headers });
            setQuestions(res.data.questions || []);
            toast.success(t('reviewShield.experienceApplied', 'Question Set applied'));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to apply experience');
        } finally {
            setBusyExp(false);
        }
    };

    const handleDelete = async (q) => {
        if (!window.confirm(t('reviewShield.confirmDeleteQuestion', 'Delete this question? This cannot be undone.'))) return;
        try {
            await axios.delete(withOrg(`${API_BASE}/api/review-shield/questions/${q.id}`), { headers });
            setQuestions(prev => prev.filter(x => x.id !== q.id));
            toast.success(t('reviewShield.questionDeleted', 'Question deleted'));
        } catch (err) {
            toast.error(err.response?.data?.detail || t('reviewShield.cannotDeleteQuestion', 'Cannot delete question'));
        }
    };

    const handleToggle = async (q) => {
        try {
            const res = await axios.put(withOrg(`${API_BASE}/api/review-shield/questions/${q.id}/toggle`), {}, { headers });
            setQuestions(prev => prev.map(x => x.id === q.id ? { ...x, is_active: res.data.is_active } : x));
        } catch (err) {
            toast.error(err.response?.data?.detail || t('reviewShield.cannotDeactivateLast'));
        }
    };

    const handleMove = async (q, direction) => {
        const idx = questions.findIndex(x => x.id === q.id);
        if ((direction === 'up' && idx <= 0) || (direction === 'down' && idx >= questions.length - 1)) return;
        const newOrder = [...questions];
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
        [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
        setQuestions(newOrder);
        try {
            await axios.put(withOrg(`${API_BASE}/api/review-shield/questions/reorder`),
                { question_ids: newOrder.map(x => x.id) },
                { headers }
            );
        } catch (err) {
            loadQuestions(); // Revert on failure
        }
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setFormText('');
    };

    const startEdit = (q) => {
        setEditingId(q.id);
        setFormText(q[`text_${primaryLang}`] || q.text_en || '');
        setShowForm(true);
    };

    const startCreate = () => {
        setEditingId(null);
        setFormText('');
        setShowForm(true);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="question-manager">
            <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">{t('reviewShield.questionManager', 'Question Manager')}</Label>
                {!showForm && (
                    <Button type="button" variant="outline" size="sm" onClick={startCreate} data-testid="add-question-btn">
                        <Plus className="w-3.5 h-3.5 mr-1" />{t('reviewShield.addQuestion', 'Add Question')}
                    </Button>
                )}
            </div>

            {/* Create / Edit Form */}
            {showForm && (
                <div className="p-3 border rounded-lg bg-slate-50 dark:bg-slate-800/50 space-y-2" data-testid="question-form">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {editingId ? t('reviewShield.editQuestion', 'Edit Question') : t('reviewShield.addQuestion', 'Add Question')}
                    </p>
                    <div>
                        <Label className="text-xs">{t('reviewShield.questionText', 'Question')} <span className="text-muted-foreground">({langLabel})</span></Label>
                        <Input
                            value={formText}
                            onChange={(e) => setFormText(e.target.value)}
                            placeholder={t('reviewShield.questionPlaceholder', 'Type the question in your language…')}
                            data-testid="question-input-primary"
                            autoFocus
                        />
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {t('reviewShield.autoLangHint', 'English, French and Spanish are generated automatically.')}
                        </p>
                    </div>
                    <div className="flex gap-2 pt-1">
                        <Button type="button" size="sm" onClick={editingId ? handleUpdate : handleCreate} disabled={saving || !formText.trim()} data-testid="question-save-btn">
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                            {t('reviewShield.save', 'Save')}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={resetForm}>
                            <X className="w-3.5 h-3.5 mr-1" />{t('reviewShield.cancel', 'Cancel')}
                        </Button>
                    </div>
                </div>
            )}

            {/* Questions List */}
            {questions.length === 0 && !showForm ? (
                <p className="text-sm text-muted-foreground text-center py-4" data-testid="no-questions-msg">
                    {t('reviewShield.noQuestions', 'No questions yet. Create one to get started.')}
                </p>
            ) : (
                <div className="space-y-1.5" data-testid="questions-list">
                    {questions.map((q, idx) => (
                        <div
                            key={q.id}
                            className={`flex items-center gap-2 p-2.5 border rounded-lg text-sm transition-colors ${!q.is_active ? 'opacity-50 bg-slate-50 dark:bg-slate-800/30' : ''}`}
                            data-testid={`question-row-${q.id}`}
                        >
                            {/* Text */}
                            <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{q.text_en}</p>
                                <p className="text-xs text-muted-foreground truncate">{q.text_fr}</p>
                                {q.text_es ? <p className="text-xs text-muted-foreground truncate" data-testid={`question-es-${q.id}`}>{q.text_es}</p> : null}
                            </div>

                            {/* Controls — every question is editable and persists immediately. */}
                            <div className="flex items-center gap-0.5 shrink-0">
                                <button onClick={() => handleMove(q, 'up')} disabled={idx === 0}
                                    className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30"
                                    data-testid={`question-move-up-${q.id}`}>
                                    <ChevronUp className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => handleMove(q, 'down')} disabled={idx === questions.length - 1}
                                    className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30"
                                    data-testid={`question-move-down-${q.id}`}>
                                    <ChevronDown className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => startEdit(q)}
                                    className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                                    title={t('reviewShield.editQuestion', 'Edit')}
                                    data-testid={`question-edit-${q.id}`}>
                                    <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => handleToggle(q)}
                                    className={`p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 ${q.is_active ? 'text-emerald-600' : 'text-slate-400'}`}
                                    title={q.is_active ? 'Deactivate' : 'Activate'}
                                    data-testid={`question-toggle-${q.id}`}>
                                    {q.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                                </button>
                                <button onClick={() => handleDelete(q)}
                                    className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"
                                    title={t('reviewShield.deleteQuestion', 'Delete')}
                                    data-testid={`question-delete-${q.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Save as / apply reusable Review Shield Experience */}
            {organizationId && questions.length > 0 && (
                <div className="pt-3 mt-2 border-t space-y-2" data-testid="experiences-section">
                    {!showSaveExp ? (
                        <Button type="button" variant="outline" size="sm" onClick={() => setShowSaveExp(true)} data-testid="save-experience-btn">
                            <Bookmark className="w-3.5 h-3.5 mr-1" />{t('reviewShield.saveAsExperience', 'Save as Question Set')}
                        </Button>
                    ) : (
                        <div className="flex items-center gap-2" data-testid="save-experience-form">
                            <Input
                                value={expName}
                                onChange={(e) => setExpName(e.target.value)}
                                placeholder={t('reviewShield.experienceNamePlaceholder', 'e.g., Hotel, Auto Dealer, Dentist')}
                                className="h-9"
                                data-testid="experience-name-input"
                            />
                            <Button type="button" size="sm" onClick={handleSaveExperience} disabled={busyExp || !expName.trim()} data-testid="experience-save-confirm">
                                {busyExp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => { setShowSaveExp(false); setExpName(''); }}>
                                <X className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                    )}

                    {experiences.length > 0 && (
                        <div className="space-y-1.5" data-testid="experiences-list">
                            <p className="text-xs text-muted-foreground">{t('reviewShield.savedExperiences', 'Saved Question Sets')}</p>
                            {experiences.map((exp) => {
                                const expUrl = `${PUBLIC_BASE}/review-shield-experience/${exp.id}`;
                                const copyUrl = async () => {
                                    try {
                                        await navigator.clipboard.writeText(expUrl);
                                        toast.success(t('reviewShield.experienceUrlCopied', 'Public URL copied'));
                                    } catch {
                                        toast.error(t('reviewShield.experienceUrlCopyFailed', 'Could not copy URL'));
                                    }
                                };
                                return (
                                <div key={exp.id} className="p-2.5 border rounded-md text-sm space-y-2" data-testid={`experience-${exp.id}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="truncate font-medium" data-testid={`experience-name-${exp.id}`}>{exp.name} <span className="text-muted-foreground font-normal">({(exp.questions || []).length})</span></span>
                                        <Button type="button" variant="outline" size="sm" onClick={() => handleApplyExperience(exp)} disabled={busyExp} data-testid={`experience-apply-${exp.id}`}>
                                            <FolderInput className="w-3.5 h-3.5 mr-1" />{t('reviewShield.apply', 'Apply')}
                                        </Button>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('reviewShield.permanentPublicUrl', 'Permanent public URL')}</p>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-[11px] text-foreground/80" data-testid={`experience-url-${exp.id}`}>{expUrl}</code>
                                            <Button type="button" variant="outline" size="sm" onClick={copyUrl} data-testid={`experience-copy-url-${exp.id}`}>
                                                <Copy className="w-3.5 h-3.5 mr-1" />{t('reviewShield.copyUrl', 'Copy URL')}
                                            </Button>
                                            <Button type="button" variant="outline" size="sm" onClick={() => window.open(expUrl, '_blank', 'noopener,noreferrer')} data-testid={`experience-open-${exp.id}`}>
                                                <ExternalLink className="w-3.5 h-3.5 mr-1" />{t('reviewShield.openPreview', 'Open / Preview')}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
